import { Component, Inject, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatPaginator } from '@angular/material';
import { ObservableMedia, MediaChange } from '@angular/flex-layout';

import { first } from 'rxjs/operators/first';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators/takeUntil';

import { DataTableSource } from '../../../classes/DataTableSource/datatablesource';

import { ApplicationService, UserSearchResult } from '../../application/application.service';
import { SplashService } from '../../splash/splash.service';
import { UserInfo, UserService } from '../../user/user.service';

@Component({
    selector: 'dialog-usersearch-component',
    styleUrls: ['usersearch.dialogcomponent.css'],
    templateUrl: 'usersearch.dialogcomponent.html',
})
export class UserSearchDialogComponent implements OnInit, OnDestroy {
    @ViewChild('paginator') paginator: MatPaginator;

    private _subrelease: Subject<void> = new Subject<void>();

    AllHeaders: string[] = [
        'LastName',
        'FirstName',
        'City',
        'Email',
        // "Username",
        'Actions',
    ];
    canceltext: string;
    DesigneeMode: boolean;
    forcedLocationNumber: string;
    LastSizeCode: string;
    message: string;
    oktext: string;
    SearchForm: FormGroup;
    SearchReady: boolean;
    ShowSearch: boolean;
    SmallDisplay: boolean;
    title: string;
    CanAddAsAdministrator: boolean;
    User: UserInfo;
    UserList: UserSearchResult[];
    UserSearchConducted: boolean;
    UserData: DataTableSource<UserSearchResult>;
    VisibleHeaders: string[];
    VisibleColumns: string[];

    constructor(
        private appService: ApplicationService,
        private fb: FormBuilder,
        public dialogRef: MatDialogRef<UserSearchDialogComponent>,
        @Inject(MAT_DIALOG_DATA) data: BaseUserSearchDialogOptions,
        private mediaChange: ObservableMedia,
        private splashService: SplashService,
        private userService: UserService
    ) {
        this.canceltext = data.CancelText || 'Cancel';
        this.message = data.MessageText || '';
        this.oktext = data.OKText || 'Select';
        this.title = data.TitleText || 'User Search';
        this.CanAddAsAdministrator = data.CanAddAsAdministrator != null ? data.CanAddAsAdministrator : false;
        this.DesigneeMode = data.DesigneeMode != null ? data.DesigneeMode : false;
    }

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    ngOnInit() {
        const self = this;
        self.SearchReady = false;
        self.ShowSearch = true;

        self.LastSizeCode = self.mediaChange.isActive('xs')
            ? 'xs'
            : self.mediaChange.isActive('sm')
            ? 'sm'
            : self.mediaChange.isActive('md')
            ? 'md'
            : self.mediaChange.isActive('lg')
            ? 'lg'
            : 'xl';
        self.SmallDisplay = self.LastSizeCode === 'xs' || self.LastSizeCode === 'sm';

        self.mediaChange
            .asObservable()
            .pipe(takeUntil(self._subrelease))
            .subscribe(media => {
                self.SmallDisplay = media.mqAlias === 'xs' || media.mqAlias === 'sm';
                self.LastSizeCode = media.mqAlias;
                self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
                self.VisibleColumns =
                    !self.UserList || !self.UserList.length
                        ? ['NoData']
                        : self.SmallDisplay
                        ? ['SmallDisplay']
                        : self.VisibleHeaders.slice();
            });

        // Wait for first non-null user object
        self.userService.UserSubject.pipe(
            takeUntil(self._subrelease),
            first(_user => _user && !!_user.AuthToken)
        ).subscribe(user => {
            self.User = user;

            // Set up constant monitoring of the user object, to catch AuthToken updates.
            self.userService.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(_user => {
                self.User = _user;
            });

            this.SearchForm = this.fb.group(
                {
                    FirstName: [''],
                    LastName: [''],
                },
                {
                    validator: function(ctrl: FormGroup) {
                        if (!ctrl) {
                            return null;
                        }

                        const form = ctrl.value;

                        if (!form.FirstName && !form.LastName) {
                            return { noneprovided: true };
                        }

                        return null;
                    },
                }
            );

            this.UserList = null;
            this.UserData = new DataTableSource<UserInfo>([], self.paginator);
            this.UserSearchConducted = false;

            self.SearchReady = true;
        });
    }

    // #region Cancel Handler Method

    CancelHandler() {
        // User cancelled.
        this.dialogRef.close(null);
    }

    // #endregion

    // #region Designee Handler Method

    DesigneeHandler() {
        // User has opted to create a new account.
        this.dialogRef.close({
            User: null,
        });
    }

    // #endregion

    // #region Designee OK Handler Method

    DesigneeOKHandler(user: UserSearchResult, AsAdmin?: boolean) {
        // User selected a search result. Pass back.
        this.dialogRef.close({
            User: user,
            AccessLevel: 0,
        } as BaseUserSearchResultInfo);
    }

    // #endregion

    // #region Handle User Key Method

    HandleUserKey(key: any) {
        this.HandleUserSearch();
    }

    // #endregion

    // #region Handle User Search Method

    HandleUserSearch() {
        const self = this;

        const form = self.SearchForm.value;

        if (!form.FirstName && !form.LastName) {
            self.splashService.SnackbarError('You must provide at least a portion of the first name or last name in order to search.');
            return;
        }

        if (self.SearchForm.invalid) {
            return;
        }

        // Call the search for the user
        self.appService.SearchForUser(self.User.AuthToken, form.FirstName, form.LastName).subscribe(
            users => {
                // Update into table with search results
                self.UserList = users;
                self.ShowSearch = false;

                self.UserData.Update(users && users.length ? users : [{}]);

                self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
                self.VisibleColumns =
                    !self.UserList || !self.UserList.length
                        ? ['NoData']
                        : self.SmallDisplay
                        ? ['SmallDisplay']
                        : self.VisibleHeaders.slice();

                self.UserSearchConducted = true;
            },
            error => {
                let msg: string = null;

                if (error instanceof Response) {
                    const body = <any>error.json();
                    msg = body.Message || (typeof body === 'string' ? body : 'An unknown error has occurred.');
                } else {
                    let errorPacket = error.message || error;
                    if (errorPacket != null && errorPacket !== '') {
                        errorPacket = JSON.parse(errorPacket);
                    }
                    msg =
                        errorPacket != null && errorPacket instanceof Object
                            ? errorPacket.ExceptionMessage || errorPacket.Message || error
                            : errorPacket || 'An unknown error has occurred.';
                }

                if (msg) {
                    self.splashService.SnackbarError(msg);
                }
            }
        );
    }

    // #endregion

    // #region OK Handler Method

    OKHandler(user: UserSearchResult, AccessLevel?: number) {
        // User selected a search result. Pass back.
        this.dialogRef.close({
            User: user,
            AccessLevel: AccessLevel,
        } as BaseUserSearchResultInfo);
    }

    // #endregion

    // #region Show Search Form Method

    ShowSearchForm() {
        this.ShowSearch = true;
        this.UserSearchConducted = false;
    }

    // #endregion
}

// #region Custom Classes

// #region User Search Dialog Options Class

export class BaseUserSearchDialogOptions {
    TitleText?: string;
    MessageText?: string;
    OKText?: string;
    CancelText?: string;
    IconColor?: string;
    IconName?: string;
    ShowIcon?: boolean;
    height?: string;
    width?: string;
    CanAddAsAdministrator?: boolean;
    DesigneeMode?: boolean;
}

// #endregion

// #region User Search Result Info Class

export class BaseUserSearchResultInfo {
    User?: UserSearchResult;
    AccessLevel?: number;
}

// #endregion

// #endregion



// WEBPACK FOOTER //
// ./src/services/dialogs/usersearch/usersearch.dialogcomponent.ts